import * as React from "react";
import { useResource, Resources } from "Translations/Resources";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { useRouteMatch } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getUsersDetailAsync,
  updateUserDetailAsync,
} from "State/Users/UserReducer";
import { RootStateType } from "State/Store";
import { PrimarySkeleton } from "Components/Shared/PrimarySkeleton";
import { UserAccessRoles } from "Components/Settings/Users/UserAccessRoles";
import styled from "styled-components";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { UserAccessRoleDto } from "Api/Api";
import { setDisableRedirect } from "State/App/AppReducer";
import { formatDate } from "Utils/DateUtils";
import { BreadcrumbItem } from "Components/Layout/Breadcrumbs";
import { StyledWrapper } from "Components/Companies/CompanyEdit/CompanyEditBasicInfo/CompanyEditBasicInfoStyles";
import { TextInput } from "Components/Shared/Inputs/TextInput";
import { Select, MenuItem, Typography } from "@material-ui/core";

type RouteProps = {
  id: string;
};

const StyledBasicInfo = styled.div`
  margin-bottom: ${props => props.theme.spacing(4)}px;

  .ibis-MuiInputBase-input.Mui-disabled {
    background-color: ${props => props.theme.colors.border};
    border-radius: ${props => props.theme.borderRadius};
    cursor: not-allowed;
  }

  .login {
    grid-area: 2/1;
  }
  .status-select {
    grid-area: 2/2;
  }

  .selectbox {
    .ibis-MuiInputBase-root {
      display: flex;

      &:before,
      &:after {
        display: none;
      }

      .ibis-MuiSelect-nativeInput {
        top: 0;
      }

      .ibis-MuiSelect-select.ibis-MuiSelect-select {
        border: 1px solid ${props => props.theme.colors.border};
        border-radius: ${props => props.theme.borderRadius};
        padding: 9px 12px;
      }

      &.Mui-focused,
      &:focus {
        background-color: transparent;
      }

      .ibis-MuiSelect-select {
        &:focus {
          background-color: transparent;
        }
      }

      .ibis-MuiSvgIcon-root {
        right: 5px;
      }
    }
  }
`;

const StyledTitle = styled.label`
  color: ${props => props.theme.palette.secondary.main};
  display: inline-block;
`;

const StyledLastLoginDate = styled.div`
  & > p {
    font-weight: 300;
    color: ${props => props.theme.palette.secondary.main};
    margin-bottom: 5px;
  }

  ${props => props.theme.breakpoints.down("xs")} {
    max-width: 100%;
  }
`;

export const UserDetailPage: React.FunctionComponent = _ => {
  const { t } = useResource();
  const { params } = useRouteMatch<RouteProps>();
  const userID = params.id as any;
  const dispatch = useDispatch();
  const { userDetail, isLoading, notValid } = useSelector(
    (e: RootStateType) => e.users,
  );
  const [roles, setRoles] = React.useState<UserAccessRoleDto[]>([]);
  const [isActive, setIsActive] = React.useState<boolean>(false);

  React.useEffect(() => {
    dispatch(getUsersDetailAsync.request(userID));
  }, [userID, dispatch]);

  React.useEffect(() => {
    if (userDetail?.accessRoles) {
      setRoles(userDetail.accessRoles);
    }
  }, [userDetail, setRoles]);

  React.useEffect(() => {
    if (userDetail) {
      setIsActive(userDetail.isActive);
    }
  }, [userDetail, setIsActive]);

  React.useEffect(() => {
    if (userDetail && notValid) {
      setIsActive(userDetail.isActive);

      if (userDetail.accessRoles) {
        setRoles(userDetail.accessRoles);
      }
    }
    // eslint-disable-next-line
  }, [notValid]);

  const save = () => {
    if (userDetail) {
      const userUpdateDto = {
        userID: userDetail.userID,
        accessRolesIDs: roles.filter(x => x.hasRole).map(x => x.accessRoleID),
        isActive: isActive,
        employeeID: userDetail.employeeID,
      };

      dispatch(updateUserDetailAsync.request(userUpdateDto));
      dispatch(setDisableRedirect(false));
    }
  };

  const handleChangeSelectbox = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    const selectedValue = event.target.value as string;
    setIsActive(selectedValue === "true");
    dispatch(setDisableRedirect(true));
  };

  const getStringInput = (
    name: string,
    label: string,
    value: string,
    isDisabled = false,
  ) => (
    <div className={name}>
      <TextInput
        label={label}
        name={name}
        value={value}
        disabled={isDisabled}
      />
    </div>
  );

  const getBreadcrumbItems = () => {
    return [
      {
        name: t(Resources.Users.Title),
        url: t(Resources.Routes.Users),
      } as BreadcrumbItem,
      {
        name: t(Resources.Users.Detail.Breadcrumb.Title),
      } as BreadcrumbItem,
    ];
  };

  return (
    <AuthenticatedLayout
      title={`${userDetail?.firstName} ${userDetail?.lastName}`}
      breadcrumbItems={getBreadcrumbItems()}
    >
      <div className={"container-small"}>
        <div className={"box detail"}>
          <div className={"box-header"}>
            {!isLoading && userDetail ? (
              t(Resources.Users.Detail.Title)
            ) : (
              <PrimarySkeleton height={30} />
            )}
          </div>
          <div className={"box-content"}>
            {!isLoading && userDetail ? (
              <>
                <div className={"form-title"}>
                  {t(Resources.Users.Detail.BasicInfo.Title)}
                </div>
                <StyledBasicInfo>
                  <StyledWrapper>
                    {getStringInput(
                      "firstName",
                      t(Resources.Users.Detail.BasicInfo.FirstName),
                      userDetail.firstName,
                      true,
                    )}

                    {getStringInput(
                      "lastName",
                      t(Resources.Users.Detail.BasicInfo.LastName),
                      userDetail.lastName,
                      true,
                    )}

                    {getStringInput(
                      "login",
                      t(Resources.Users.Detail.BasicInfo.Login),
                      userDetail.login,
                      true,
                    )}

                    <div className={"selectbox status-select"}>
                      <StyledTitle className={"form-label"}>
                        {t(Resources.Users.Detail.BasicInfo.Status)}
                      </StyledTitle>
                      <Select
                        id={"status-select"}
                        value={isActive.toString()}
                        onChange={handleChangeSelectbox}
                      >
                        <MenuItem value={"true"}>
                          {t(Resources.Users.Detail.Status.IsActive)}
                        </MenuItem>
                        <MenuItem value={"false"}>
                          {t(Resources.Users.Detail.Status.InActive)}
                        </MenuItem>
                      </Select>
                    </div>
                  </StyledWrapper>
                </StyledBasicInfo>

                <StyledLastLoginDate>
                  <Typography>
                    {t(Resources.Users.Detail.LastLoginDate.Title)}
                  </Typography>
                  <div>{formatDate(userDetail.lastLoginDate)}</div>
                </StyledLastLoginDate>

                <UserAccessRoles roles={roles} setRoles={setRoles} />

                <PrimaryButton onClick={save}>
                  {t(Resources.Users.Detail.Save)}
                </PrimaryButton>
              </>
            ) : (
              <>
                <PrimarySkeleton height={60} />
                <PrimarySkeleton height={60} />
                <PrimarySkeleton height={60} />
                <PrimarySkeleton height={60} />
                <PrimarySkeleton height={60} />
                <PrimarySkeleton height={60} />
              </>
            )}
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
};
